import { ChangeEvent, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { IModalMessage } from "../../models/dto/generalApi/ICheckInVsAbsence";
import activationsService from "../../services/agendaApi/activations.service";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { VeneerFileInputFileList } from "@veneer/core";
import { Workbook, Cell, CellValue } from "exceljs";
import {
  BulkResult,
  IJsonDate,
} from "../../models/dto/agendaApi/IActivationAssignmentDto";

export const UseBulkActivationAssignment = () => {
  const [t] = useTranslation("global");
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState<string>("");
  const [errorFilePicker, setErrorFilePicker] = useState(false);
  const [buttonNext, setButtonNext] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [jsonDate, setJsonDate] = useState<IJsonDate[]>([]);
  const [fileError, setFileError] = useState<string>("");
  const [modalMessage, setModalMessage] = useState<IModalMessage>({
    show: false,
    message: "",
    title: "",
    onClose: function () {},
  });

  const onClickHelper = () => {
    setModalMessage({
      show: true,
      message: (
        <Trans
          i18nKey="modal.helperAssignmentOfActivations"
          components={{ br: <br /> }}
          ns="global"
        />
      ),
      title: t("modal.titleHelperAssignmentOfActivations"),
      onClose: () =>
        setModalMessage((prevState) => ({ ...prevState, show: false })),
    });
  };

  const onClickDownloadTemplate = async () => {
    try {
      setLoading(true);

      const result = await activationsService.getAssignmentOfActivationsExcel(
        languageID.value
      );

      if (result && typeof result === "object" && "statusCode" in result) {
        throw new Error(`Error ${result.statusCode}`);
      }

      const blob = result as Blob;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "assignmentOfActivations.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setModalMessage({
        show: true,
        message: t("header.errorDownloadingFile"),
        title: t("header.error"),
        onClose: () => setModalMessage((prev) => ({ ...prev, show: false })),
      });
    } finally {
      setLoading(false);
    }
  };

  /** Extrae valor “resuelto” de una celda (valor directo, resultado de fórmula, texto de hipervínculo, concatenado de rich text…) */
  function getCellResolved(
    cell?: Cell
  ): string | number | boolean | Date | undefined {
    if (!cell) return undefined;
    const v = cell.value as any as CellValue | undefined;
    if (v == null) return undefined;

    if (v instanceof Date) return v;
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    )
      return v;

    if (typeof v === "object") {
      // Fórmula: toma el resultado
      if ("formula" in v) return (v as any).result;
      // Hipervínculo: toma el texto
      if ("text" in v && typeof (v as any).text === "string")
        return (v as any).text;
      // Rich text: concatena los textos
      if ("richText" in v && Array.isArray((v as any).richText)) {
        return (v as any).richText.map((rt: any) => rt.text).join("");
      }
      // Otros objetos (por ejemplo, error): toma “result” si existe
      if ("result" in v) return (v as any).result;
    }
    return undefined;
  }

  /** Convierte número serial Excel a fecha ISO (YYYY-MM-DD) */
  function excelSerialToISO(n: number): string | undefined {
    if (typeof n !== "number") return undefined;
    const base = Date.UTC(1899, 11, 30);
    const ms = Math.round(n * 86400000);
    const d = new Date(base + ms);
    return isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
  }

  /** Convierte valor de celda a fecha ISO (YYYY-MM-DD) si es posible */
  function toISODate(val: any): string | undefined {
    if (val == null) return undefined;
    if (val instanceof Date) return val.toISOString().slice(0, 10);
    if (typeof val === "number") return excelSerialToISO(val);
    if (typeof val === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      const t = Date.parse(val);
      if (!isNaN(t)) return new Date(t).toISOString().slice(0, 10);
    }
    return undefined;
  }

  function isMeaningfulRow(row: {
    posCode?: any;
    startISO?: string;
    endISO?: string;
    activation?: any;
    subretail?: any;
  }) {
    return Boolean(row.posCode && row.startISO && row.endISO);
  }

  // Definición de columnas (1-based)
  // E: startDate, F: endDate, Z: activationID, AA: subretailID, AB: posCode
  const COL = { E: 5, F: 6, Z: 26, AA: 27, AB: 28 };

  const onChangeFilePicker = async (
    event: ChangeEvent<HTMLInputElement>,
    files: VeneerFileInputFileList,
    inputValue: string
  ) => {
    try {
      const file =
        (files && files[0]) || (event?.target?.files && event.target.files[0]);

      const ext = file.name.split(".").pop()?.toLowerCase();
      if (ext !== "xlsx") {
        setErrorFilePicker(true);
        setButtonNext(true);
        setFileError(t("header.messageXLSX"));
        return;
      }

      const buf = await file.arrayBuffer();
      const wb = new Workbook();
      await wb.xlsx.load(buf);

      const ws = wb.worksheets[0];
      if (!ws) {
        setErrorFilePicker(true);
        setButtonNext(true);
        setFileError(t("modal.theFileHasNoSheets"));
        return;
      }

      const out: IJsonDate[] = [];
      const errors: Array<{ row: number; field: string; value: any }> = [];

      setFileName(file.name);

      ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber === 1) return; // cabecera

        const activation = getCellResolved(row.getCell(COL.Z));
        const subretail = getCellResolved(row.getCell(COL.AA));
        const pos = getCellResolved(row.getCell(COL.AB));

        const startRaw = getCellResolved(row.getCell(COL.E));
        const endRaw = getCellResolved(row.getCell(COL.F));
        const startISO = toISODate(startRaw);
        const endISO = toISODate(endRaw);

        if (
          !isMeaningfulRow({
            posCode: pos,
            startISO,
            endISO,
            activation,
            subretail,
          })
        ) {
          return;
        }

        if (startISO == null)
          errors.push({ row: rowNumber, field: "startDate", value: startRaw });
        if (endISO == null)
          errors.push({ row: rowNumber, field: "endDate", value: endRaw });

        const activationID = Number(activation);
        const subretailID = Number(subretail);

        const item: IJsonDate = {
          activationID: activationID,
          subretailID: subretailID,
          details: [
            {
              posCode: String(pos ?? "").trim(),
              startDate: startISO!,
              endDate: endISO!,
            },
          ],
        };

        out.push(item);
      });
      if (errors.length) {
        setErrorFilePicker(true);
        setButtonNext(true);
        setFileError(t("modal.errorsDetectedInTheExcelFile"));
      }

      if (out.length === 0) {
        setErrorFilePicker(true);
        setButtonNext(true);
        setFileError(t("modal.noDataToSubmit"));
      }

      setJsonDate(out);
      setButtonNext(false);
    } catch (err) {
      setErrorFilePicker(true);
      setButtonNext(true);
      setFileError(t("modal.errorReadingTheExcelFile"));
    } finally {
      if (event?.target) event.target.value = "";
    }
  };

  const onClear = () => {
    setFileName("");
    setButtonNext(true);
    setErrorFilePicker(false);
  };

  const handleOnClickExit = () => {
    setOpenModal(false);
    setFileName("");
    setButtonNext(true);
  };

  const handleOnClickOpen = () => {
    setFileName("");
    setButtonNext(true);
    setOpenModal(true);
    setErrorFilePicker(false);
  };

  const submitInBulk = async (payload: IJsonDate[]) => {
    try {
      setLoading(true);
      const result =
        await activationsService.createAssignmentOfActivationInBulk(payload);

      if (result && typeof result === "object" && "statusCode" in result) {
        throw new Error(`Error ${result.statusCode}`);
      }

      if (Array.isArray(result)) {
        await exportBulkResultToExcel(result);
      }

      setModalMessage({
        show: true,
        message: t("modal.assignationCreated"),
        title: t("modal.message"),
        onClose: () => setModalMessage((prev) => ({ ...prev, show: false })),
      });
    } catch (error) {
      setModalMessage({
        show: true,
        message: t("modal.generalError"),
        title: t("modal.error"),
        onClose: () => setModalMessage((prev) => ({ ...prev, show: false })),
      });
    } finally {
      setLoading(false);
      handleOnClickExit();
    }
  };

  // Función para normalizar fechas a YYYY-MM-DD
  function toISODateString(input: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input; // ya es ISO
    const core = input.slice(0, 10); // "MM/DD/YYYY"
    const parts = core.split("/");
    if (parts.length === 3) {
      const [mm, dd, yyyy] = parts;
      return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    }
    // fallback
    const t = Date.parse(input);
    return isNaN(t) ? input : new Date(t).toISOString().slice(0, 10);
  }

  // Función para exportar con exceljs
  async function exportBulkResultToExcel(rows: BulkResult[]) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Resultados");

    // Cabeceras
    worksheet.columns = [
      { header: "Activación", key: "activationName", width: 30 },
      { header: "Subretail", key: "subretailName", width: 25 },
      { header: "POS", key: "posName", width: 15 },
      { header: "Inicio", key: "startDate", width: 15 },
      { header: "Fin", key: "endDate", width: 15 },
      { header: "Detalle", key: "detail", width: 50 },
    ];

    // Filas
    rows.forEach((r) => {
      worksheet.addRow({
        activationName: r.activationName,
        subretailName: r.subretailName,
        posName: r.posName,
        startDate: toISODateString(r.startDate),
        endDate: toISODateString(r.endDate),
        detail: r.detail,
      });
    });

    // Generar archivo para descarga en navegador
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "resultado_asignaciones.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  }

  return {
    modalMessage,
    onClickHelper,
    onClickDownloadTemplate,
    loading,
    handleOnClickOpen,
    handleOnClickExit,
    openModal,
    onChangeFilePicker,
    onClear,
    fileName,
    errorFilePicker,
    buttonNext,
    submitInBulk,
    jsonDate,
    fileError,
  };
};
